import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { ResponseData } from 'src/app/shared/interfaces/response.interface';
import { BonusApi } from '../apis/bonus.api';
import { AppService } from '../../app.service';
import { LocaleService } from './locale.service';
import { LocalStorageService } from './localstorage.service';
import { ToastService } from './toast.service';

@Injectable({
  providedIn: 'root',
})
export class VoucherClaimService {
  private readonly destroyRef = inject(DestroyRef);
  private readonly exchangeErrorCodes = new Set(['2142', '2143', '2144']);
  private readonly pendingVoucherStorageKey = 'storage.platform.pending-voucher-code';
  // v2 excludes vouchers that older code cached after an unsuccessful business response.
  private readonly claimedVoucherStorageKey = 'storage.platform.claimed-voucher-codes-v2';

  private initialized = false;
  private voucherCode: string | null = null;
  private readonly claimingVouchers = new Set<string>();
  private readonly completedVouchers = new Set<string>();
  private readonly failedVouchers = new Set<string>();

  constructor(
    private appService: AppService,
    private route: ActivatedRoute,
    private router: Router,
    private bonusApi: BonusApi,
    private localStorageService: LocalStorageService,
    private localeService: LocaleService,
    private toast: ToastService,
  ) {}

  init(): void {
    if (this.initialized) return;
    this.initialized = true;

    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(queryParams => {
      const queryVoucher = queryParams.get('voucher')?.trim() || null;
      const voucher = queryVoucher || this.getPendingVoucher();

      if (voucher && this.hasClaimedVoucher(voucher)) {
        this.voucherCode = voucher;
        this.clearPendingVoucher(voucher);
        if (queryVoucher) this.clearVoucherQuery(voucher);
        return;
      }

      if (queryVoucher) this.savePendingVoucher(queryVoucher);
      if (voucher === this.voucherCode) {
        this.claimVoucherIfPossible();
        return;
      }

      this.voucherCode = voucher;
      this.claimVoucherIfPossible();
    });

    this.appService.userInfo$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(userInfo => {
        if (userInfo) this.claimVoucherIfPossible();
      });
  }

  private claimVoucherIfPossible(): void {
    const voucher = this.voucherCode;
    if (!voucher || !this.localStorageService.loginToken) return;
    if (this.hasClaimedVoucher(voucher)) return;
    if (
      this.claimingVouchers.has(voucher) ||
      this.completedVouchers.has(voucher) ||
      this.failedVouchers.has(voucher)
    ) {
      return;
    }

    this.claimingVouchers.add(voucher);
    this.bonusApi.setExchangeReceive({ exchangeCode: voucher }).subscribe({
      next: response => {
        this.claimingVouchers.delete(voucher);

        if (response === null) {
          // BaseApi already shows the network error toast for null fallbacks.
          this.failedVouchers.add(voucher);
          this.clearPendingVoucher(voucher);
          this.clearVoucherQuery(voucher);
          return;
        }

        if (response.data) {
          this.completedVouchers.add(voucher);
          this.saveClaimedVoucher(voucher);
        } else {
          this.failedVouchers.add(voucher);
        }
        this.showExchangeResult(response);
        this.clearPendingVoucher(voucher);
        this.clearVoucherQuery(voucher);
      },
      error: () => {
        this.claimingVouchers.delete(voucher);
        this.failedVouchers.add(voucher);
        this.showExchangeFailure();
        this.clearPendingVoucher(voucher);
        this.clearVoucherQuery(voucher);
      },
    });
  }

  private getPendingVoucher(): string | null {
    return window.sessionStorage.getItem(this.pendingVoucherStorageKey)?.trim() || null;
  }

  private savePendingVoucher(voucher: string): void {
    window.sessionStorage.setItem(this.pendingVoucherStorageKey, voucher);
  }

  private hasClaimedVoucher(voucher: string): boolean {
    return this.getClaimedVouchers().has(voucher);
  }

  private saveClaimedVoucher(voucher: string): void {
    const claimedVouchers = this.getClaimedVouchers();
    claimedVouchers.add(voucher);
    window.sessionStorage.setItem(this.claimedVoucherStorageKey, JSON.stringify([...claimedVouchers]));
  }

  private getClaimedVouchers(): Set<string> {
    const storedVouchers = window.sessionStorage.getItem(this.claimedVoucherStorageKey);
    if (!storedVouchers) return new Set<string>();

    try {
      const vouchers: unknown = JSON.parse(storedVouchers);
      return new Set(
        Array.isArray(vouchers)
          ? vouchers.filter((voucher): voucher is string => typeof voucher === 'string')
          : [],
      );
    } catch {
      return new Set<string>();
    }
  }

  private clearPendingVoucher(voucher: string): void {
    if (this.getPendingVoucher() === voucher) {
      window.sessionStorage.removeItem(this.pendingVoucherStorageKey);
    }
  }

  private showExchangeResult(response: ResponseData<boolean> | null): void {
    if (response?.data) {
      this.toast.show({ message: `${this.localeService.getValue('exchange_succ')}`, type: 'success' });
      return;
    }

    const message = this.exchangeErrorCodes.has(String(response?.code))
      ? response?.message
      : this.localeService.getValue('exchange_fail');
    this.toast.show({
      message: `${message || this.localeService.getValue('exchange_fail')}`,
      type: 'fail',
    });
  }

  private showExchangeFailure(): void {
    this.toast.show({
      message: `${this.localeService.getValue('exchange_fail')}`,
      type: 'fail',
    });
  }

  private clearVoucherQuery(voucher: string): void {
    if (this.voucherCode !== voucher) return;

    void this.router
      .navigate([], {
        relativeTo: this.route,
        queryParams: { voucher: null },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      })
      .catch(() => undefined);
  }
}
